/**
 * TODO - preamble
 */
import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    MedicalStatusComponent as MedicalStatus,
    MedicalProfessionalOrganizationComponent as MedicalProfessionalOrganization,
    MedicalProfessionalStatusDefaultMessagesEnum,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    BirthDateComponent as BirthDate,
    EmailComponent as Email,
    PostalCodeComponent as PostalCode,
    FormFooterComponent as FormFooter,
    PhoneNumberComponent as PhoneNumber,
    VerificationStepsEnum,
    isFormFilled,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    hasFailedInstantMatch,
    updateViewModelOrganization,
    updateFieldValidationErrorsByFieldId,
    handleEmailOnKeyDown,
    submitForm,
    setRef,
    setFocus,

    // Types:
    FormSelectChoice,
    VerificationService,
    MedicalProfessionalPersonalInfoViewModel,
    FieldValidationErrors,
    Organization,
    MedicalProfessionalPersonalInfoResponse,
    ExtendedFieldValidationErrors,
    InputSelectOnKeyDown,
    Metadata,
 } from '../../es6';
import { MarketConsentWrapperComponent } from '../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent';
import { LogoComponent } from '../LogoComponent/LogoComponent';

interface StepMedicalProfessionalPersonalInfoComponentProps {
    verificationService: VerificationService;
    intl: any;
}

export const StepMedicalProfessionalPersonalInfo = ({ intl, verificationService }: StepMedicalProfessionalPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as MedicalProfessionalPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateMedicalProfessionalViewModel = (key: keyof MedicalProfessionalPersonalInfoViewModel, value: any) => {
        const nextState: MedicalProfessionalPersonalInfoViewModel = produce(viewModel, (draft: MedicalProfessionalPersonalInfoViewModel) => {
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const getAvailableMedicalStatuses = ():FormSelectChoice[] => {
        const availableStatusesResponse =
            (verificationService.verificationResponse as MedicalProfessionalPersonalInfoResponse).availableStatuses;
        const availableStatuses: FormSelectChoice[] = [];

        for (const status of availableStatusesResponse) {
            availableStatuses.push({
                value: status,
                label: intl.formatMessage({ id: status, defaultMessage: MedicalProfessionalStatusDefaultMessagesEnum[status] }),
            });
        }
        return availableStatuses;
    };

    const handleMedicalStatusOnKeyDown: InputSelectOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            setFocus('organization');
        }
    };

    return (
        <div id="sid-step-active-medical-professional-personal-info" className="sid-l-container">
            { failedInstantMatch ?
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name, Email & Organization" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name, organization name, and email must match what is on record with your agency." />
                    </div>
                </div>
            :
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Medical Professional-Only Offer" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a licensed medical professional." />
                    </div>
                    <div className="sid-header__how-verifying-works sid-l-horz-center">
                        <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                </div>
            }

            <MedicalStatus
                value={{
                    value: (verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel).status,
                    label: MedicalProfessionalStatusDefaultMessagesEnum[(verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel).status],
                }}
                isErrored={!!fieldValidationErrors.status}
                options={ getAvailableMedicalStatuses() }
                onChange={
                    (status: FormSelectChoice) => {
                        updateMedicalProfessionalViewModel('status', status ? (status.value as string) : '');
                        updateFieldValidationErrorsByFieldId('status', status ? (status.value as string) : '', verificationService);
                        if (status) {
                            setFocus('organization');
                        }
                    }
                }
                onKeyDown={event => handleMedicalStatusOnKeyDown(event)}
            />

            <MedicalProfessionalOrganization
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
                value={(verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel).organization}
                verificationService={verificationService}
            />

            <div className="sid-names">
                <FirstName

                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue) => {
                            updateMedicalProfessionalViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName

                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue) => {
                            updateMedicalProfessionalViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue) => {
                    updateMedicalProfessionalViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <PostalCode
                isErrored={!!fieldValidationErrors.postalCode}
                onChange={(newValue) => {
                    updateMedicalProfessionalViewModel('postalCode', newValue);
                    updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                }}
                value={viewModel.postalCode}
                explanation={<FormattedHTMLMessage
                    id="postalCodeExplanation"
                    defaultMessage="Use the location on file with your licensing body"
                />}
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Needed to send you your unique code"
                    />
                }
                onChange={
                    (newValue) => {
                        updateMedicalProfessionalViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={event => handleEmailOnKeyDown(event)}
            />
            {
                verificationService.programTheme.isSmsNotifierConfigured ?
                    <PhoneNumber
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue) => {
                                updateMedicalProfessionalViewModel('phoneNumber', newValue);
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                    : null
            }
            <MarketConsentWrapperComponent
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateMedicalProfessionalViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectMedicalProfessionalPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={button => setRef('submitButton', button)}
                    >
                        { failedInstantMatch ?
                            <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                        :
                            <FormattedHTMLMessage id="verifyMyMedicalProfessionalStatus" defaultMessage="Verify My Medical Professional Status" />
                        }
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="MedicalProfessionalInfoShared"
                    defaultMessage={'Information entered here will be used for verification purposes.'}
                />
            </FormFooter>
        </div>
    );
};

export const StepMedicalProfessionalPersonalInfoComponent = injectIntl(StepMedicalProfessionalPersonalInfo);
